import { ReactNode } from "react";
import dynamic from "next/dynamic";
import { HotelResponse } from "@/lib/Types/HotelInfoResponse/HotelInfoResponse";
import MainFacilities from "../components/MainFacilities";
import { Text, Placeholder } from "@lmn/design-system";
import styled from "styled-components";
import media from "@/styles/mediaQueries";

const EmbeddedMap = dynamic(
  () => import("@/components/Stateful/Map/EmbeddedMap"),
  {
    ssr: false,
    loading: () => <Placeholder width="324px" height="168px" />,
  }
);
import { useMapStore } from "@/lib/stores/mapStore";
import { ParsedHotelDescription } from "@/lib/Helpers/parseHotelDescription";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { EnvConfig } from "@/lib/Helpers/getServerConfig";
import EmbeddedMapErrorBoundary from "@/components/Stateful/Map/EmbeddedMapErrorBoundary";

type TabOverviewProps = {
  hotelInfoData: HotelResponse;
  mapBoxApiKey: string;
  onSeeAllAmenities: () => void;
  onSeeHotelInfo: () => void;
  hasCategoryListData: boolean;
  parsedDescription: ParsedHotelDescription | null;
  fallbackDescriptionElement: ReactNode;
  envConfig: EnvConfig;
  searchId: string;
};

const Wrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  padding: "0 16px",
  ...media.screenMdMin({
    flexDirection: "row",
    width: "100%",
    padding: "0 24px",
  }),
}));

const FacilityWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: 1,
}));

const SurroundingsWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  position: "relative",
  ...media.screenMdMin({
    width: "324px",
    flexShrink: 0,
  }),
}));



const TabLink = styled(Text)`
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  &:hover {
    color: ${({ theme }) =>
    theme.kallisto.color.text.interactive.textPrimaryHover};
  }
  &:active {
    color: ${({ theme }) =>
    theme.kallisto.color.text.interactive.textPrimaryPressed};
  }
  ${media.screenMdMin`
    margin-top: 8px;
  `}
`;

const DescriptionPreview = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-line;
`;


export default function TabOverview({
  hotelInfoData,
  mapBoxApiKey,
  onSeeAllAmenities,
  onSeeHotelInfo,
  hasCategoryListData,
  parsedDescription,
  fallbackDescriptionElement,
  envConfig,
  searchId,
}: TabOverviewProps) {
  const setShowMap = useMapStore((state) => state.setShowMap);

  const headlines = hotelInfoData.hotelData.headlines || [];

  const normalizedGeo = hotelInfoData.hotelData.geo
    ? {
        accuracy: hotelInfoData.hotelData.geo.accuracy,
        lat: hotelInfoData.hotelData.geo.latitude,
        long: hotelInfoData.hotelData.geo.longitude,
      }
    : null;

  const firstSection = parsedDescription?.sections?.[0]?.content;
  const secondSection = parsedDescription?.sections?.[1]?.content;
  const descriptionPreview = firstSection || secondSection
    ? [firstSection, secondSection].filter(Boolean).join("\n\n")
    : null;


  return (
    <Wrapper>
      <FacilityWrapper>
        {hasCategoryListData ? (
          <>
            <Text variant="Title5" forceTag="h3">Hauptausstattung</Text>
            <MainFacilities headlines={headlines} />
            <TabLink onClick={onSeeAllAmenities} variant="BodySmall">
              Alle Leistungen und Ausstattungen ansehen
            </TabLink>
          </>
        ) : (
          <>
            <Text variant="Title5" forceTag="h3">Hotelinformationen</Text>
            <DescriptionPreview>
              {descriptionPreview ? (
                <Text variant="BodySmall">{descriptionPreview}</Text>
              ) : (
                <Text variant="BodySmall">{fallbackDescriptionElement}</Text>
              )}
            </DescriptionPreview>
            <TabLink onClick={onSeeHotelInfo} variant="BodySmall">
              Alle Hotelinfos ansehen
            </TabLink>
          </>
        )}
      </FacilityWrapper>
      {normalizedGeo && (
        <SurroundingsWrapper>
          <Text variant="Title5" forceTag="h3">In der Umgebung</Text>
          <EmbeddedMapErrorBoundary envConfig={envConfig} searchId={searchId}>
            <EmbeddedMap
              lat={normalizedGeo.lat}
              long={normalizedGeo.long}
              mapBoxApiKey={mapBoxApiKey}
              width="100%"
              height="168px"
              onError={() =>
                sendKibanaLog({
                  envConfig,
                  message: "EmbeddedMap WebGL init failed",
                  category: "error",
                  searchId,
                })
              }
              onOpenMap={() => {
                setShowMap(true);
                sendKibanaLog({ envConfig, message: "Map Opened from Overview Tab", category: "interaction", searchId });
              }}
            />
          </EmbeddedMapErrorBoundary>
        </SurroundingsWrapper>
      )}
    </Wrapper>
  );
}
